import { useState, useEffect, useCallback, useRef } from 'react';
import { BackendWebSocketService, KlineData, fetchHistoricalFromBackend, controlWebSocketService } from '../services';
import { logger } from '../utils/logger';

export interface UseBackendRealtimeDataProps {
  symbol?: string;
  interval?: string;
  historicalLimit?: number;
  fallbackData?: any[]; // Fallback to local JSON data
  autoStart?: boolean; // 是否自动启动WebSocket服务
}

export interface UseBackendRealtimeDataReturn {
  data: KlineData[];
  isConnected: boolean;
  latestPrice?: number;
  lastUpdateTime?: number;
  error?: string;
  isLoading: boolean;
  reconnect: () => void;
  updateSubscription: (symbol: string, interval: string) => void;
  lastUpdateType?: 'initial' | 'newCandle' | 'updateCandle';
  lastUpdatedCandle?: KlineData;
  connectionStatus: 'connected' | 'disconnected' | 'reconnecting';
  // 新增的服务控制方法
  startService: () => Promise<void>;
  stopService: () => Promise<void>;
  getServiceStatus: () => Promise<any>;
}

export const useBackendRealtimeData = ({
  symbol = 'BTCUSDT',
  interval = '1h',
  historicalLimit = 10000,
  fallbackData = [],
  autoStart = true
}: UseBackendRealtimeDataProps = {}): UseBackendRealtimeDataReturn => {
  const [data, setData] = useState<KlineData[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'disconnected' | 'reconnecting'>('disconnected');
  const [latestPrice, setLatestPrice] = useState<number>();
  const [lastUpdateTime, setLastUpdateTime] = useState<number>();
  const [error, setError] = useState<string>();
  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdateType, setLastUpdateType] = useState<'initial' | 'newCandle' | 'updateCandle'>();
  const [lastUpdatedCandle, setLastUpdatedCandle] = useState<KlineData>();

  const wsServiceRef = useRef<BackendWebSocketService | null>(null);
  const initializationRef = useRef(false);
  const serviceStartedRef = useRef(false);

  // Convert fallback data to KlineData format
  const convertFallbackData = useCallback((fallbackData: any[]): KlineData[] => {
    return fallbackData.map(item => ({
      time: typeof item.time === 'number' ? item.time : Math.floor(new Date(item.time || item.datetime).getTime() / 1000),
      open: Number(item.open),
      high: Number(item.high),
      low: Number(item.low),
      close: Number(item.close),
      volume: Number(item.volume || 0)
    }));
  }, []);

  // Handle kline updates from backend WebSocket
  const handleKlineUpdate = useCallback((kline: KlineData) => {
    let updateType: 'updateCandle' | 'newCandle' = 'updateCandle';

    setData(prevData => {
      const newData = [...prevData];
      const lastIndex = newData.length - 1;
      const lastTime = lastIndex >= 0 ? newData[lastIndex].time : 0;

      // If this is an update to the current candle (same time), replace it
      if (lastIndex >= 0 && lastTime === kline.time) {
        newData[lastIndex] = kline;
        updateType = 'updateCandle';
      } else if (lastIndex >= 0 && kline.time < lastTime) {
        // Out-of-order (older) update; ignore to keep array strictly ascending
        updateType = 'updateCandle';
        return newData;
      } else {
        // This is a new candle, add it
        newData.push(kline);
        updateType = 'newCandle';

        // Keep only recent data (maintain performance)
        if (newData.length > historicalLimit + 100) {
          newData.splice(0, 100); // Remove oldest 100 items
        }
      }

      return newData;
    });

    // Update state with new kline info
    setLastUpdateType(updateType);
    setLastUpdatedCandle(kline);
    setLatestPrice(kline.close);
    setLastUpdateTime(Math.floor(Date.now() / 1000));
    setError(undefined);
  }, [historicalLimit]);

  // Handle connection status changes
  const handleConnectionStatusChange = useCallback((status: 'connected' | 'disconnected' | 'reconnecting') => {
    setConnectionStatus(status);
    setIsConnected(status === 'connected');

    if (status === 'connected') {
      setError(undefined);
    }
  }, []);

  // Handle WebSocket errors
  const handleError = useCallback((errorMessage: string) => {
    setError(errorMessage);
    logger.error('Backend WebSocket error:', errorMessage);
  }, []);

  // Handle data updated notification
  const handleDataUpdated = useCallback(() => {
    logger.log('📝 Data updated notification received, reloading fallback data...');
    // 重新加载本地数据
    if (fallbackData.length > 0) {
      const convertedData = convertFallbackData(fallbackData);
      setData(prevData => {
        // 合并新数据，避免重复
        const existingTimes = new Set(prevData.map(d => d.time));
        const newData = convertedData.filter(d => !existingTimes.has(d.time));

        if (newData.length > 0) {
          logger.log(`Added ${newData.length} new data points from local file`);
          return [...prevData, ...newData].sort((a, b) => a.time - b.time);
        }
        return prevData;
      });
    }
  }, [fallbackData, convertFallbackData]);

  // Initialize data
  const initializeData = useCallback(async () => {
    if (initializationRef.current) {
      return; // Prevent multiple initializations
    }

    setIsLoading(true);
    setError(undefined);

    try {
      logger.log(`Initializing data for ${symbol}@${interval}...`);

      // Always start with fallback data if available
      if (fallbackData.length > 0) {
        const convertedData = convertFallbackData(fallbackData);
        setData(convertedData);
        setLatestPrice(convertedData[convertedData.length - 1]?.close);
        setLastUpdateType('initial');
        logger.log(`Loaded ${convertedData.length} fallback data points for ${symbol}`);
      }

      // Try to fetch recent data from backend or Binance API
      try {
        const { data: recentData } = await fetchHistoricalFromBackend(
          symbol,
          interval,
          Math.min(historicalLimit, 10000)
        );

        if (recentData && recentData.length > 0) {
          // Merge fallback data with recent data
          if (fallbackData.length > 0) {
            const convertedCache = convertFallbackData(fallbackData);
            const lastCacheTime = convertedCache[convertedCache.length - 1]?.time || 0;

            // Filter out overlapping data and append new candles
            const newCandles = recentData.filter((candle: KlineData) => candle.time > lastCacheTime);

            if (newCandles.length > 0) {
              const mergedData = [...convertedCache, ...newCandles]
                .sort((a, b) => a.time - b.time)
                .filter((d, idx, arr) => idx === 0 || d.time !== arr[idx - 1].time);
              setData(mergedData);
              setLatestPrice(mergedData[mergedData.length - 1]?.close);
              logger.log(`✅ Merged with ${newCandles.length} new candles from API for ${symbol}`);
            }
          } else {
            // No fallback data, use API data only
            const sorted = [...recentData]
              .sort((a, b) => a.time - b.time)
              .filter((d, idx, arr) => idx === 0 || d.time !== arr[idx - 1].time);
            setData(sorted);
            setLatestPrice(recentData[recentData.length - 1]?.close);
            logger.log(`✅ Loaded ${recentData.length} data points from API for ${symbol}`);
          }
          setLastUpdateType('initial');
        }
      } catch (apiError) {
        logger.warn('Failed to fetch from API, using fallback data only:', apiError);

        // If API fails but we have fallback data, that's OK
        if (fallbackData.length === 0) {
          setError('No data available from server or cache');
        }
      }
    } catch (error) {
      logger.error('Failed to initialize data:', error);
      setError('Failed to load initial data');
    } finally {
      setIsLoading(false);
      initializationRef.current = true;
    }
  }, [symbol, interval, historicalLimit, fallbackData, convertFallbackData]);

  // Initialize WebSocket service
  const initializeWebSocket = useCallback(() => {
    if (wsServiceRef.current) {
      wsServiceRef.current.disconnect();
    }

    logger.log('Initializing WebSocket connection...');
    wsServiceRef.current = new BackendWebSocketService(
      handleKlineUpdate,
      handleConnectionStatusChange,
      handleError,
      handleDataUpdated,
      symbol,
      interval
    );
    wsServiceRef.current.setExpected(symbol, interval);

    wsServiceRef.current.connect();
  }, [handleKlineUpdate, handleConnectionStatusChange, handleError, handleDataUpdated, symbol, interval]);

  // Service control methods
  const startService = useCallback(async () => {
    try {
      logger.log('Starting WebSocket service...');
      const result = await controlWebSocketService('start');

      if (result.success) {
        logger.log('WebSocket service started');
        // 等待一下然后连接
        setTimeout(() => {
          initializeWebSocket();
        }, 1000);
      } else {
        throw new Error(result.message || 'Failed to start service');
      }
    } catch (error) {
      logger.error('Failed to start WebSocket service:', error);
      setError(`Failed to start service: ${error}`);
    }
  }, [initializeWebSocket]);

  const stopService = useCallback(async () => {
    try {
      logger.log('Stopping WebSocket service...');

      // 先断开客户端连接
      if (wsServiceRef.current) {
        wsServiceRef.current.disconnect();
      }

      // 然后停止服务
      const result = await controlWebSocketService('stop');

      if (result.success) {
        logger.log('WebSocket service stopped');
      } else {
        throw new Error(result.message || 'Failed to stop service');
      }
    } catch (error) {
      logger.error('Failed to stop WebSocket service:', error);
      setError(`Failed to stop service: ${error}`);
    }
  }, []);

  const getServiceStatus = useCallback(async () => {
    try {
      return await controlWebSocketService('status');
    } catch (error) {
      logger.error('Failed to get service status:', error);
      throw error;
    }
  }, []);

  // Reconnect function
  const reconnect = useCallback(() => {
    setError(undefined);
    if (wsServiceRef.current) {
      logger.log('Reconnecting...');
      wsServiceRef.current.disconnect();
      setTimeout(() => {
        initializeWebSocket();
      }, 1000);
    } else {
      initializeWebSocket();
    }
  }, [initializeWebSocket]);

  // Update subscription - fetch new data for different symbol/interval
  const updateSubscription = useCallback(async (newSymbol: string, newInterval: string) => {
    logger.log(`Updating subscription from ${symbol}@${interval} to ${newSymbol}@${newInterval}`);

    setIsLoading(true);
    setError(undefined);

    // Clear existing data immediately to prevent mixing
    setData([]);
    setLatestPrice(undefined);
    setLastUpdateType('initial');

    // Reset expected pair for WS filtering
    if (wsServiceRef.current) {
      wsServiceRef.current.setExpected(newSymbol, newInterval);
    }

    try {
      // Fetch new historical data for the new symbol/interval
      const { data: recentData } = await fetchHistoricalFromBackend(
        newSymbol,
        newInterval,
        Math.min(historicalLimit, 10000)
      );

      if (recentData && recentData.length > 0) {
        setData(recentData);
        setLatestPrice(recentData[recentData.length - 1]?.close);
        setLastUpdateType('initial');
        logger.log(`Updated to ${newSymbol}@${newInterval}: ${recentData.length} data points`);
        logger.log(`Sample data for ${newSymbol}:`, recentData.slice(0, 2));
      } else {
        logger.log(`No data available for ${newSymbol}@${newInterval}, keeping current data`);
        // If no data, set empty array to clear the chart
        setData([]);
        setLatestPrice(undefined);
      }

      // Reconnect WebSocket for BTC symbols (real-time data)
      if (newSymbol === 'BTCUSDT') {
        logger.log('Reconnecting WebSocket for BTC real-time data...');
        setTimeout(() => {
          initializeWebSocket();
        }, 500);
      } else {
        logger.log(`No WebSocket needed for ${newSymbol} (uses API data)`);
        setConnectionStatus('disconnected');
      }
    } catch (err) {
      logger.error(`Failed to fetch data for ${newSymbol}@${newInterval}:`, err);
      setError(`Failed to load data for ${newSymbol}@${newInterval}`);
      // Clear data on error to prevent showing wrong data
      setData([]);
      setLatestPrice(undefined);
    } finally {
      setIsLoading(false);
    }
  }, [symbol, interval, historicalLimit, initializeWebSocket]);

  // Initialize on mount (guarded to avoid repeated starts)
  useEffect(() => {
    const initialize = async () => {
      await initializeData();

      if (autoStart && !serviceStartedRef.current) {
        serviceStartedRef.current = true;
        try {
          const status = await getServiceStatus();
          if (!status.data?.is_running) {
            await startService();
          } else {
            initializeWebSocket();
          }
        } catch (error) {
          logger.log('Service status unknown, attempting to start...');
          await startService();
        }
      }
    };

    initialize();

    return () => {
      if (wsServiceRef.current) {
        wsServiceRef.current.disconnect();
      }
    };
    // Intentionally run once on mount; guarded by refs
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    data,
    isConnected,
    latestPrice,
    lastUpdateTime,
    error,
    isLoading,
    reconnect,
    updateSubscription,
    lastUpdateType,
    lastUpdatedCandle,
    connectionStatus,
    startService,
    stopService,
    getServiceStatus
  };
};
